import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getCampaigns } from '../../services/campaigns';
import { trafficQualityService } from '../../services/trafficQuality';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui';
import { cn } from '../../lib/utils';
import TrafficQualityPage from '../campaigns/TrafficQualityPage';
import type { TrafficQualityPeriod, AccountTrafficQuality } from '../../types/trafficQuality';

const PERIODS: { key: string; value: TrafficQualityPeriod }[] = [
  { key: 'today', value: 'today' },
  { key: '7d', value: 'last_7_days' },
  { key: '30d', value: 'last_30_days' },
  { key: '90d', value: 'last_90_days' },
];

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}
function ratioColor(ratio: number): string {
  if (ratio >= 70) return 'text-red-600 font-semibold';
  if (ratio >= 40) return 'text-yellow-600 font-medium';
  return 'text-gray-600';
}

export default function AccountTrafficQualityPage() {
  const { t } = useTranslation('trafficQuality');
  const [period, setPeriod] = useState<TrafficQualityPeriod>('last_7_days');
  const [selected, setSelected] = useState<number | 'overall'>('overall');

  const campaignsQ = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => getCampaigns(),
  });
  const campaigns = campaignsQ.data?.campaigns ?? [];

  const summaryQ = useQuery({
    queryKey: ['tq-account', period],
    queryFn: () => trafficQualityService.getAccountSummary(period),
    enabled: selected === 'overall',
  });

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <div className="flex items-center gap-3">
          <select
            value={String(selected)}
            onChange={(e) => setSelected(e.target.value === 'overall' ? 'overall' : parseInt(e.target.value, 10))}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
          >
            <option value="overall">{t('account.overall_option')}</option>
            {campaigns.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {selected === 'overall' && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-md transition-colors',
                    period === p.value ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  {t(`periods.${p.key}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {selected !== 'overall' ? (
        // Per-campaign view (reuses the campaign Traffic Quality page)
        <TrafficQualityPage campaignId={selected} />
      ) : summaryQ.isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : summaryQ.isError ? (
        (summaryQ.error as any)?.response?.status === 403 ? (
          <Alert variant="warning">{t('errors.locked_account')}</Alert>
        ) : (
          <Alert variant="error">{t('errors.load_failed')}</Alert>
        )
      ) : summaryQ.data ? (
        <OverallView data={summaryQ.data} />
      ) : null}
    </div>
  );
}

function OverallView({ data }: { data: AccountTrafficQuality }) {
  const { t } = useTranslation('trafficQuality');
  const s = data.summary;
  return (
    <div className="space-y-6">
      {/* Headline cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-500">{t('cards.account_quality_score')}</div>
          <div className={cn('text-4xl font-bold mt-1', scoreColor(s.quality_score))}>
            {s.quality_score}<span className="text-lg text-gray-400">/100</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.campaigns_count', { count: s.campaigns_count })}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.bot_clicks')}</div>
          <div className="text-4xl font-bold mt-1 text-red-600">{s.bot.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.bot_of_targetable', { percent: s.bot_percentage })}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-500">{t('cards.open_alerts')}</div>
          <div className="text-4xl font-bold mt-1 text-orange-600">{s.open_alerts}</div>
          <div className="text-xs text-gray-400 mt-1">{t('cards.open_alerts_hint')}</div>
        </Card>
      </div>

      {/* Per-campaign breakdown */}
      <Card header={<h2 className="font-semibold text-gray-900">{t('account.breakdown_title')}</h2>}>
        {data.campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-4">{t('account.table.campaign')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.score')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.clicks')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.bots')}</th>
                  <th className="py-2 pr-4 text-right">{t('account.table.bot_pct')}</th>
                  <th className="py-2 text-right">{t('account.table.alerts')}</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-gray-50">
                    <td className="py-2 pr-4">
                      <Link to={`/campaigns/${c.campaign_id}/traffic-quality`} className="text-primary-600 hover:underline">
                        {c.name || `#${c.campaign_id}`}
                      </Link>
                    </td>
                    <td className={cn('py-2 pr-4 text-right font-medium', scoreColor(c.quality_score))}>{c.quality_score}</td>
                    <td className="py-2 pr-4 text-right">{c.total.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right text-red-600">{c.bot.toLocaleString()}</td>
                    <td className={cn('py-2 pr-4 text-right', ratioColor(c.bot_percentage))}>{c.bot_percentage}%</td>
                    <td className="py-2 text-right">{c.open_alerts > 0 ? <span className="text-orange-600 font-medium">{c.open_alerts}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-6 text-center">{t('account.empty')}</p>
        )}
      </Card>
    </div>
  );
}
